import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Search,
  RefreshCw,
  Download,
  MoreHorizontal,
  Copy,
  CreditCard,
  ClipboardCheck,
  Mail,
  Shield,
  ShieldOff,
  RotateCcw,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

type Row = {
  user_id: string;
  email: string | null;
  full_name: string | null;
  avatar_url: string | null;
  signed_up_at: string;
  is_admin: boolean;
  is_paid: boolean;
  paid_amount: number | null;
  paid_currency: string | null;
  paid_at: string | null;
  membership_expires_at: string | null;
  assessment_status: string | null;
  assessment_id: string | null;
  assessment_started_at: string | null;
  assessment_completed_at: string | null;
  assessment_updated_at: string | null;
  last_activity_at: string;
  total_count: number;
};

type Filter = "all" | "paid" | "free" | "completed" | "in_progress" | "expiring" | "admins";
type Sort = "signup_desc" | "signup_asc" | "activity_desc" | "expiry_asc";
type Range = "all" | "7d" | "30d" | "90d";

const fmtDate = (s?: string | null) =>
  s ? new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";
const fmtShort = (s?: string | null) =>
  s ? new Date(s).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—";

const fmtMoney = (amt: number | null, ccy: string | null) => {
  if (amt == null) return "—";
  const c = (ccy || "INR").toUpperCase();
  try {
    return new Intl.NumberFormat(c === "INR" ? "en-IN" : "en-US", {
      style: "currency",
      currency: c,
      maximumFractionDigits: 0,
    }).format(amt);
  } catch {
    return `${c} ${amt}`;
  }
};

const daysUntil = (s?: string | null) => {
  if (!s) return null;
  const diff = new Date(s).getTime() - Date.now();
  return Math.round(diff / (1000 * 60 * 60 * 24));
};

const rangeToFrom = (r: Range): string | null => {
  if (r === "all") return null;
  const d = { "7d": 7, "30d": 30, "90d": 90 }[r];
  return new Date(Date.now() - d * 86400000).toISOString();
};

const toCsv = (rows: Row[]) => {
  const headers = [
    "Email",
    "Full name",
    "Signed up",
    "Admin",
    "Paid",
    "Paid amount",
    "Paid at",
    "Membership expires",
    "Assessment status",
    "Assessment completed at",
    "Last activity",
  ];
  const lines = rows.map((r) => [
    r.email ?? "",
    r.full_name ?? "",
    r.signed_up_at,
    r.is_admin ? "yes" : "",
    r.is_paid ? "yes" : "",
    r.paid_amount != null ? `${r.paid_currency || "INR"} ${r.paid_amount}` : "",
    r.paid_at ?? "",
    r.membership_expires_at ?? "",
    r.assessment_status ?? "",
    r.assessment_completed_at ?? "",
    r.last_activity_at,
  ]);
  return [headers, ...lines]
    .map((row) =>
      row
        .map((c) => {
          const s = String(c ?? "");
          return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(","),
    )
    .join("\n");
};

const STATUS_FILTERS: { value: Filter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "paid", label: "Paid" },
  { value: "free", label: "Free" },
  { value: "completed", label: "Completed test" },
  { value: "in_progress", label: "In progress" },
  { value: "expiring", label: "Expiring <30d" },
  { value: "admins", label: "Admins" },
];

const UsersTable = ({ currentUserId }: { currentUserId?: string | null }) => {
  const { toast } = useToast();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [range, setRange] = useState<Range>("all");
  const [sort, setSort] = useState<Sort>("signup_desc");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [resetting, setResetting] = useState<string | null>(null);
  const [resetTarget, setResetTarget] = useState<Row | null>(null);
  const [resetReason, setResetReason] = useState("");


  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [debounced, filter, range, sort, pageSize]);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc("admin_list_users", {
      _search: debounced || null,
      _filter: filter,
      _from: rangeToFrom(range),
      _to: null,
      _sort: sort,
      _limit: pageSize,
      _offset: page * pageSize,
    });
    if (error) {
      toast({ title: "Failed to load users", description: error.message, variant: "destructive" });
      setRows([]);
    } else {
      setRows((data ?? []) as Row[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, filter, range, sort, pageSize, page]);

  const total = rows[0]?.total_count ?? 0;
  const totalPages = Math.max(1, Math.ceil(Number(total) / pageSize));

  const kpis = useMemo(() => {
    const paid = rows.filter((r) => r.is_paid).length;
    const done = rows.filter(
      (r) => r.assessment_status === "completed" || r.assessment_status === "scored",
    ).length;
    const expiring = rows.filter((r) => {
      const d = daysUntil(r.membership_expires_at);
      return d != null && d > 0 && d < 30;
    }).length;
    return { matched: Number(total), paid, done, expiring };
  }, [rows, total]);

  const handleExport = async () => {
    setExporting(true);
    const { data, error } = await supabase.rpc("admin_list_users", {
      _search: debounced || null,
      _filter: filter,
      _from: rangeToFrom(range),
      _to: null,
      _sort: sort,
      _limit: 5000,
      _offset: 0,
    });
    setExporting(false);
    if (error) {
      toast({ title: "Export failed", description: error.message, variant: "destructive" });
      return;
    }
    const csv = toCsv((data ?? []) as Row[]);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `wekit-users-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyEmail = (email: string | null) => {
    if (!email) return;
    navigator.clipboard.writeText(email);
    toast({ title: "Email copied", description: email });
  };

  const toggleAdmin = async (row: Row) => {
    if (!row.user_id) return;
    if (row.user_id === currentUserId) {
      toast({ title: "Can't change your own admin role", variant: "destructive" });
      return;
    }
    if (row.is_admin) {
      const { error } = await supabase
        .from("user_roles")
        .delete()
        .eq("user_id", row.user_id)
        .eq("role", "admin");
      if (error) {
        toast({ title: "Failed", description: error.message, variant: "destructive" });
        return;
      }
      toast({ title: "Admin revoked", description: row.email ?? "" });
    } else {
      const { error } = await supabase
        .from("user_roles")
        .insert({ user_id: row.user_id, role: "admin" });
      if (error) {
        toast({ title: "Failed", description: error.message, variant: "destructive" });
        return;
      }
      toast({ title: "Admin granted", description: row.email ?? "" });
    }
    load();
  };

  /**
   * Give a learner their test back.
   *
   * The RPC archives finished takes (which is what the 5-take cadence gate
   * counts), closes any live session and clears device-side progress. Sealed
   * reports and the response log are never destroyed — they are the record.
   */
  const confirmReset = async () => {
    const row = resetTarget;
    const reason = resetReason.trim();
    if (!row?.user_id || reason.length < 4 || resetting) return;
    setResetting(row.user_id);
    const { data, error } = await supabase.rpc("admin_reset_assessment", {
      _user_id: row.user_id,
      _reason: reason,
    });
    setResetting(null);
    if (error) {
      toast({ title: "Reset failed", description: error.message, variant: "destructive" });
      return;
    }
    setResetTarget(null);
    setResetReason("");
    const result = (data ?? {}) as Record<string, number>;
    toast({
      title: "Assessment reset",
      description: `${row.email ?? row.user_id} · ${result.assessments_archived ?? 0} take(s) archived, ${result.sessions_closed ?? 0} session(s) closed`,
    });
    load();
  };


  const renderAssessment = (r: Row) => {
    const s = r.assessment_status;
    if (!s) return <Badge variant="outline">Not started</Badge>;
    if (s === "completed" || s === "scored")
      return (
        <div className="flex flex-col">
          <Badge variant="default" className="w-fit">Completed</Badge>
          <span className="text-xs text-muted-foreground mt-1">
            {fmtShort(r.assessment_completed_at ?? r.assessment_updated_at)}
          </span>
        </div>
      );
    return <Badge variant="secondary">In progress</Badge>;
  };

  const renderExpiry = (r: Row) => {
    if (!r.membership_expires_at) return <span className="text-muted-foreground">—</span>;
    const d = daysUntil(r.membership_expires_at);
    const expired = d != null && d <= 0;
    const soon = d != null && d > 0 && d < 30;
    return (
      <div className="flex flex-col">
        <span className="text-sm">{fmtShort(r.membership_expires_at)}</span>
        <span
          className={`text-xs ${
            expired ? "text-destructive" : soon ? "text-amber-600" : "text-muted-foreground"
          }`}
        >
          {expired ? "Expired" : `in ${d}d`}
        </span>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniKpi label="Matched" value={kpis.matched} />
        <MiniKpi label="Paid (page)" value={kpis.paid} />
        <MiniKpi label="Completed (page)" value={kpis.done} />
        <MiniKpi label="Expiring <30d (page)" value={kpis.expiring} />
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search email or name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10"
          />
        </div>
        <Select value={range} onValueChange={(v) => setRange(v as Range)}>
          <SelectTrigger className="w-[140px] h-10"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All time</SelectItem>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="90d">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={(v) => setSort(v as Sort)}>
          <SelectTrigger className="w-[180px] h-10"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="signup_desc">Newest signup</SelectItem>
            <SelectItem value="signup_asc">Oldest signup</SelectItem>
            <SelectItem value="activity_desc">Last activity</SelectItem>
            <SelectItem value="expiry_asc">Expiry soonest</SelectItem>
          </SelectContent>
        </Select>
        <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
          <SelectTrigger className="w-[110px] h-10"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="25">25 / page</SelectItem>
            <SelectItem value="50">50 / page</SelectItem>
            <SelectItem value="100">100 / page</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting}>
          <Download className="h-4 w-4 mr-2" /> {exporting ? "Exporting…" : "Export CSV"}
        </Button>
      </div>

      {/* Chips */}
      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${
              filter === f.value
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-card hover:bg-muted/40"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-12 bg-muted/40 animate-pulse rounded" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <p className="p-10 text-center text-sm text-muted-foreground">
              No users match your filters.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Signed up</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Assessment</TableHead>
                  <TableHead>Last activity</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.user_id}>
                    <TableCell>
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="h-8 w-8 shrink-0 rounded-full bg-muted flex items-center justify-center text-xs font-medium">
                          {(r.full_name || r.email || "?").charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium truncate flex items-center gap-1">
                            {r.email ?? "—"}
                            {r.is_admin && (
                              <Badge variant="secondary" className="ml-1 h-5 text-[10px]">
                                Admin
                              </Badge>
                            )}
                          </div>
                          {r.full_name && (
                            <div className="text-xs text-muted-foreground truncate">
                              {r.full_name}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {fmtShort(r.signed_up_at)}
                    </TableCell>
                    <TableCell>
                      {r.is_paid ? (
                        <div className="flex flex-col">
                          <Badge variant="default" className="w-fit">Paid</Badge>
                          <span className="text-xs text-muted-foreground mt-1">
                            {fmtMoney(r.paid_amount, r.paid_currency)}
                          </span>
                        </div>
                      ) : (
                        <Badge variant="outline">Free</Badge>
                      )}
                    </TableCell>
                    <TableCell>{renderExpiry(r)}</TableCell>
                    <TableCell>{renderAssessment(r)}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {fmtDate(r.last_activity_at)}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuItem onClick={() => copyEmail(r.email)}>
                            <Copy className="h-4 w-4 mr-2" /> Copy email
                          </DropdownMenuItem>
                          {r.email && (
                            <DropdownMenuItem asChild>
                              <Link to={`/admin/payments?q=${encodeURIComponent(r.email)}`}>
                                <CreditCard className="h-4 w-4 mr-2" /> View payments
                              </Link>
                            </DropdownMenuItem>
                          )}
                          {r.email && (
                            <DropdownMenuItem asChild>
                              <Link to={`/admin/payment-console?q=${encodeURIComponent(r.email)}`}>
                                <ClipboardCheck className="h-4 w-4 mr-2" /> Open in console
                              </Link>
                            </DropdownMenuItem>
                          )}
                          {r.email && (
                            <DropdownMenuItem
                              onClick={() => (window.location.href = `mailto:${r.email}`)}
                            >
                              <Mail className="h-4 w-4 mr-2" /> Email user
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => toggleAdmin(r)}
                            disabled={r.user_id === currentUserId}
                          >
                            {r.is_admin ? (
                              <>
                                <ShieldOff className="h-4 w-4 mr-2" /> Revoke admin
                              </>
                            ) : (
                              <>
                                <Shield className="h-4 w-4 mr-2" /> Grant admin
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => {
                              setResetReason("Admin reset requested");
                              setResetTarget(r);
                            }}
                            disabled={resetting !== null}
                            className="text-destructive focus:text-destructive"
                          >
                            <RotateCcw className="h-4 w-4 mr-2" />
                            {resetting === r.user_id ? "Resetting…" : "Reset assessment"}
                          </DropdownMenuItem>

                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {total > 0
            ? `Showing ${page * pageSize + 1}–${Math.min((page + 1) * pageSize, Number(total))} of ${total}`
            : "No results"}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0 || loading}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground">
            Page {page + 1} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1 || loading}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Reset confirmation — a reason is mandatory, and it lands in the audit trail. */}
      <Dialog
        open={resetTarget !== null}
        onOpenChange={(open) => {
          if (!open && !resetting) {
            setResetTarget(null);
            setResetReason("");
          }
        }}
      >
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Reset this assessment?</DialogTitle>
            <DialogDescription>
              {resetTarget?.email ?? resetTarget?.user_id} gets their test back. Completed takes
              are archived so the retake limit resets, live sessions are closed and saved progress
              is cleared. Reports and answer logs are kept.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reset-reason">Reason (recorded in the audit trail)</Label>
            <Textarea
              id="reset-reason"
              value={resetReason}
              onChange={(e) => setResetReason(e.target.value)}
              placeholder="Why are you resetting this assessment?"
              rows={3}
              disabled={resetting !== null}
            />
            {resetReason.trim().length < 4 && (
              <p className="text-xs text-muted-foreground">
                Give at least a few words so the log stays useful.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setResetTarget(null);
                setResetReason("");
              }}
              disabled={resetting !== null}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmReset}
              disabled={resetting !== null || resetReason.trim().length < 4}
              aria-busy={resetting !== null}
            >
              {resetting !== null ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Resetting…
                </>
              ) : (
                "Reset assessment"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function MiniKpi({ label, value }: { label: string; value: number | string }) {
  return (
    <Card className="rounded-xl">
      <CardContent className="py-3">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-xl font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}

export default UsersTable;
